import { useEffect, useMemo, useState } from 'react';

import BulkCompanyEmployeesModal from '@/components/features/Companies/forms/BulkCompanyEmployeesModal';
import MergeCompaniesModal from '@/components/features/Companies/forms/MergeCompaniesModal';
import { companyFilterOptions, companyTableDescriptor } from '@/components/features/Companies/tables/company-table-descriptor';
import { CompanyCard } from '@/components/features/Companies/tables/CompanyCard';
import { CompanyTable } from '@/components/features/Companies/tables/CompanyTable';
import { CreateButton } from '@/components/layout/CreateButton';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { buildToolbarDefinitions, quickFilterButtonLabel, serverSortKey, toFilterSpecMap } from '@/components/ui/embedded-resource-table/descriptor';
import { GenericToolbar, type ToolbarViewMode } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useSettings } from '@/contexts/SettingsContext';
import { useAbilities } from '@/hooks/use-abilities';
import { useBreadcrumbs } from '@/hooks/use-breadcrumbs';
import { useColumnVisibility } from '@/hooks/use-column-visibility';
import { useDebounce } from '@/hooks/use-debounce';
import { useIndexFilters } from '@/hooks/use-index-filters';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { assignableUserId, type AssignableUser } from '@/lib/assigned-user-options';
import { type Company, type PaginationMeta, type User } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import axios from 'axios';
import { Merge, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

type CompaniesPageProps = {
    companies: Company[];
    pagination: PaginationMeta;
    // Filters are multi-select, hence the array member.
    filters?: Record<string, string | number | string[] | undefined>;
    availableFilterValues?: {
        assigned_user_id: string[];
    };
    /** Every active colleague, with their roles — what the assigned-user filter groups by. */
    filterUsers?: AssignableUser[];
    [key: string]: unknown;
};

const COLUMN_STORAGE_KEY = 'companies:index';

/** Which bulk action the row checkboxes are currently feeding. */
type SelectionIntent = 'merge' | 'employees';

export default function Companies() {
    const { props } = usePage<CompaniesPageProps>();
    const { companies, pagination, filters = {}, availableFilterValues, filterUsers = [] } = props;
    const { t } = useTranslations();
    const { settings } = useSettings();

    // Columns, filters and sort entries come from the descriptor the detail-page
    // tabs read too, so the renderings of this table cannot drift apart.
    const definitions = useMemo(() => buildToolbarDefinitions(companyTableDescriptor, t, { requireServerSort: true }), [t]);
    const filterSpecs = useMemo(() => toFilterSpecMap(companyTableDescriptor), []);

    const {
        columnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters: clearServerFilters,
        applyFilters,
        loading,
    } = useIndexFilters(filterSpecs, filters, '/companies');

    const isMobile = useIsMobile();
    const [viewMode, setViewMode] = useState<ToolbarViewMode>(isMobile ? 'grid' : 'table');
    const [searchValue, setSearchValue] = useState(filters.search?.toString() ?? '');
    const debouncedSearchValue = useDebounce(searchValue, 800);
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    // Seeded from the server so the header keeps showing which column is sorted
    // after the round-trip that applied it.
    const [sortColumn, setSortColumn] = useState<string | null>(filters.sort_by?.toString() ?? null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
        filters.sort_order === 'asc' || filters.sort_order === 'desc' ? filters.sort_order : null,
    );
    const {
        visibleColumns,
        showColumn: handleColumnShow,
        hideColumn: handleColumnHide,
    } = useColumnVisibility(COLUMN_STORAGE_KEY, definitions.defaultVisibleColumns, definitions.allColumnIds);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [users, setUsers] = useState<User[]>([]);

    const { can } = useAbilities();

    /**
     * Bulk actions share one selection mechanic — rows become selectable instead
     * of clickable — but they are gated by different permissions, so the intent
     * records which action owns the current selection.
     */
    const canMergeCompanies = can('settings.manage');
    const canAssignEmployees = can('assign-employees');
    const [selectionIntent, setSelectionIntent] = useState<SelectionIntent | null>(null);
    const selectionMode = selectionIntent !== null;
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [mergeOpen, setMergeOpen] = useState(false);
    const [employeesOpen, setEmployeesOpen] = useState(false);

    const startSelection = (intent: SelectionIntent) => {
        setSelectionIntent((prev) => (prev === intent ? null : intent));
        setSelectedIds(new Set());
    };

    const exitSelection = () => {
        setSelectionIntent(null);
        setSelectedIds(new Set());
    };

    const toggleSelect = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (!next.delete(id)) next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedIds((prev) => (companies.every((company) => prev.has(company.id)) ? new Set() : new Set(companies.map((c) => c.id))));
    };

    const selectedCompanies = useMemo(
        () =>
            companies
                .filter((company) => selectedIds.has(company.id))
                .map((company) => ({
                    id: company.id,
                    name: company.name,
                    company_code: company.company_code,
                    country: company.country,
                    // Only the bulk employees sheet reads this — it builds its
                    // unassign list from who is already on the selected rows.
                    users: company.users,
                })),
        [companies, selectedIds],
    );

    // Fetch users for assigned users cell (only when the user may assign)
    useEffect(() => {
        if (!can('assign-employees')) {
            return;
        }
        const fetchUsers = async () => {
            try {
                // The bulk sheet must be able to offer every colleague, so this
                // asks for the endpoint's full ceiling rather than its default.
                const response = await axios.get('/api/users/assignable', { params: { limit: 500 } });
                if (response.data?.users) {
                    setUsers(response.data.users);
                }
            } catch (error) {
                console.error('Failed to fetch users:', error);
            }
        };
        fetchUsers();
    }, [can]);

    // Get company types, priorities and categories from settings
    const companyTypes = useMemo(() => settings.companyTypes || [], [settings.companyTypes]);
    const companyPriorities = useMemo(() => settings.companyPriorities || [], [settings.companyPriorities]);
    const companyCategories = useMemo(() => settings.companyCategories || [], [settings.companyCategories]);

    const paginationMeta = useMemo<PaginationMeta>(
        () => ({
            current_page: pagination.current_page ?? 1,
            last_page: pagination.last_page ?? 1,
            per_page: pagination.per_page ?? 10,
            total: pagination.total ?? companies.length,
            from: pagination.from ?? null,
            to: pagination.to ?? null,
        }),
        [pagination, companies.length],
    );

    useEffect(() => {
        setViewMode(isMobile ? 'grid' : 'table');
    }, [isMobile]);

    useEffect(() => {
        const nextSearch = filters.search?.toString() ?? '';
        setSearchValue((previous) => (previous === nextSearch ? previous : nextSearch));
    }, [filters.search]);

    useEffect(() => {
        if ((filters.search?.toString() ?? '') === debouncedSearchValue) {
            return;
        }

        applyFilters({
            search: debouncedSearchValue || undefined,
            page: 1,
        });
    }, [applyFilters, debouncedSearchValue, filters.search]);

    /**
     * Options come from settings (the full domain of type/priority/category) and from
     * `availableFilterValues` — never from the rows on screen.
     */
    const columnFilterOptions = useMemo(() => {
        const availableUserIds = new Set(availableFilterValues?.assigned_user_id ?? []);
        // Narrowed to colleagues who actually hold companies, so the card does not
        // list the whole roster to filter a handful of rows.
        const assignedUserOptions = availableUserIds.size ? filterUsers.filter((user) => availableUserIds.has(assignableUserId(user))) : filterUsers;

        return companyFilterOptions({
            t,
            types: companyTypes,
            priorities: companyPriorities,
            categories: companyCategories,
            users: assignedUserOptions,
        });
    }, [companyTypes, companyPriorities, companyCategories, filterUsers, availableFilterValues, t]);

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        // `uuid` is the server's own fallback for an unrecognised key.
        applyFilters({ sort_by: serverSortKey(companyTableDescriptor, column) ?? 'uuid', sort_order: direction, page: 1 });
    };

    const clearSort = () => {
        setSortColumn(null);
        setSortDirection(null);
        applyFilters({ sort_by: undefined, sort_order: undefined, page: 1 });
    };

    const clearFilters = () => {
        setSearchValue('');
        clearServerFilters();
    };

    const handlePageChange = (page: number) => {
        applyFilters({ page });
    };

    const handlePerPageChange = (perPage: number) => {
        applyFilters({ per_page: perPage, page: 1 });
    };

    // Inline editing handlers
    const handleUpdate = {
        name: async (companyId: string, value: string) => {
            router.patch(
                `/company/${companyId}`,
                {
                    name: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('companies.index.messages.name_updated', 'Company name updated'));
                    },
                    onError: () => {
                        toast.error(t('companies.index.messages.failed_update_name', 'Failed to update company name'));
                    },
                },
            );
        },
        domain: async (companyId: string, value: string | null) => {
            router.patch(
                `/company/${companyId}`,
                {
                    domain: value || null,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('companies.index.messages.domain_updated', 'Domain updated'));
                    },
                    onError: () => {
                        toast.error(t('companies.index.messages.failed_update_domain', 'Failed to update domain'));
                    },
                },
            );
        },
        type: async (companyId: string, value: string) => {
            router.patch(
                `/company/${companyId}`,
                {
                    type: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('companies.index.messages.type_updated', 'Type updated'));
                    },
                    onError: () => {
                        toast.error(t('companies.index.messages.failed_update_type', 'Failed to update type'));
                    },
                },
            );
        },
        priority: async (companyId: string, value: string) => {
            router.patch(
                `/company/${companyId}/priority`,
                {
                    priority: value,
                },
                {
                    preserveState: true,
                    preserveScroll: true,
                    onSuccess: () => {
                        toast.success(t('companies.index.messages.priority_updated', 'Priority updated'));
                    },
                    onError: () => {
                        toast.error(t('companies.index.messages.failed_update_priority', 'Failed to update priority'));
                    },
                },
            );
        },
        assignedUsers: async (companyId: string, userIds: string[]) => {
            try {
                // Get current assigned users
                const company = companies.find((c) => c.id === companyId);
                const currentUserIds = new Set((company?.users || []).map((u) => u.id));
                const newUserIds = new Set(userIds);

                // Find users to add and remove
                const toAdd = userIds.filter((id) => !currentUserIds.has(id));
                const toRemove = Array.from(currentUserIds).filter((id) => !newUserIds.has(id));

                // Remove users first
                for (const userId of toRemove) {
                    try {
                        await axios.delete(`/companies/${companyId}/remove-user/${userId}`, {
                            headers: {
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                        });
                    } catch (error) {
                        console.error(`Error removing user ${userId}:`, error);
                    }
                }

                // Add new users
                for (const userId of toAdd) {
                    try {
                        await axios.post(
                            `/companies/${companyId}/assign-user`,
                            { user_id: userId },
                            {
                                headers: {
                                    'X-Requested-With': 'XMLHttpRequest',
                                },
                            },
                        );
                    } catch (error) {
                        console.error(`Error assigning user ${userId}:`, error);
                    }
                }

                toast.success(t('companies.index.messages.users_updated', 'Assigned users updated'));
                router.reload({ only: ['companies'] });
            } catch (error) {
                console.error('Failed to update assigned users:', error);
                toast.error(t('companies.index.messages.failed_update_users', 'Failed to update assigned users'));
            }
        },
    };

    const breadcrumbs = useBreadcrumbs('companies');

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('companies.index.title', 'Companies')} />
            <PageHeader
                title={t('companies.index.heading', 'Companies')}
                subtitle={t('companies.index.subheading', 'Manage your companies and their details')}
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        {(canMergeCompanies || canAssignEmployees) && viewMode === 'table' && (
                            <>
                                {selectionIntent === 'merge' && (
                                    <Button
                                        variant="outline"
                                        disabled={selectedIds.size < 2}
                                        onClick={() => setMergeOpen(true)}
                                        title={t('companies.merge.errors.needs_two', 'Select at least two companies to merge.')}
                                    >
                                        <Merge className="mr-2 h-4 w-4" />
                                        {t('companies.merge.action_selected', 'Merge selected').replace(':count', String(selectedIds.size))}
                                    </Button>
                                )}
                                {selectionIntent === 'employees' && (
                                    <Button variant="outline" disabled={selectedIds.size === 0} onClick={() => setEmployeesOpen(true)}>
                                        <UserPlus className="mr-2 h-4 w-4" />
                                        {t('companies.bulk_employees.action_selected', 'Manage :count companies').replace(
                                            ':count',
                                            String(selectedIds.size),
                                        )}
                                    </Button>
                                )}
                                {/* Each entry button hides while the other intent owns the
                                    selection, so the two modes cannot be confused. */}
                                {canMergeCompanies && selectionIntent !== 'employees' && (
                                    <Button variant={selectionIntent === 'merge' ? 'ghost' : 'outline'} onClick={() => startSelection('merge')}>
                                        {selectionIntent === 'merge' ? (
                                            t('companies.merge.cancel', 'Cancel')
                                        ) : (
                                            <>
                                                <Merge className="mr-2 h-4 w-4" />
                                                {t('companies.merge.action', 'Merge duplicates')}
                                            </>
                                        )}
                                    </Button>
                                )}
                                {canAssignEmployees && selectionIntent !== 'merge' && (
                                    <Button
                                        variant={selectionIntent === 'employees' ? 'ghost' : 'outline'}
                                        onClick={() => startSelection('employees')}
                                    >
                                        {selectionIntent === 'employees' ? (
                                            t('companies.merge.cancel', 'Cancel')
                                        ) : (
                                            <>
                                                <UserPlus className="mr-2 h-4 w-4" />
                                                {t('companies.bulk_employees.action', 'Assign employees')}
                                            </>
                                        )}
                                    </Button>
                                )}
                            </>
                        )}
                        <CreateButton
                            href="/companies/create"
                            permission="companies.create"
                            label={t('companies.index.actions.create', 'Create Company')}
                        />
                    </div>
                }
            />

            <PageContent>
                {/* Toolbar */}
                <GenericToolbar
                    quickFilterButtonLabel={quickFilterButtonLabel(companyTableDescriptor, t, columnFilters)}
                    quickFilterDefinitions={definitions.quickFilterDefinitions}
                    columnDefinitions={definitions.columnDefinitions}
                    extraFilterDefinitions={definitions.extraFilterDefinitions}
                    columnFilterOptions={columnFilterOptions}
                    columnFilters={columnFilters}
                    columnFilterSearch={columnFilterSearch}
                    setColumnFilterSearch={setColumnFilterSearch}
                    toggleSingleFilter={toggleSingleFilter}
                    handleFilterChange={handleFilterChange}
                    clearFilters={clearFilters}
                    isFilterOpen={isFilterOpen}
                    setIsFilterOpen={setIsFilterOpen}
                    isSortOpen={isSortOpen}
                    setIsSortOpen={setIsSortOpen}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    handleSortChange={handleSortChange}
                    clearSort={clearSort}
                    search={searchValue}
                    setSearch={setSearchValue}
                    perPage={paginationMeta.per_page}
                    onPerPageChange={handlePerPageChange}
                    viewMode={viewMode}
                    setViewMode={setViewMode}
                    visibleColumns={visibleColumns}
                    handleColumnShow={handleColumnShow}
                    handleColumnHide={handleColumnHide}
                    filteredCount={paginationMeta.total}
                    resourceName={companyTableDescriptor.resourceName}
                    resourceNamePlural={companyTableDescriptor.resourceNamePlural}
                    sortableColumns={definitions.sortableColumns}
                />

                {/* Content */}
                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {companies.map((company) => (
                            <CompanyCard key={company.id} company={company} />
                        ))}
                    </div>
                ) : (
                    <CompanyTable
                        companies={companies}
                        users={users}
                        onUpdate={handleUpdate}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onSortChange={handleSortChange}
                        onColumnHide={handleColumnHide}
                        onColumnShow={handleColumnShow}
                        visibleColumns={visibleColumns}
                        columnFilters={columnFilters}
                        columnFilterOptions={columnFilterOptions}
                        onFilterChange={handleFilterChange}
                        types={companyTypes}
                        priorities={companyPriorities}
                        selectionMode={selectionMode}
                        selectedIds={selectedIds}
                        onToggleSelect={toggleSelect}
                        onToggleSelectAll={toggleSelectAll}
                    />
                )}

                {canMergeCompanies && <MergeCompaniesModal companies={selectedCompanies} open={mergeOpen} onOpenChange={setMergeOpen} />}

                {canAssignEmployees && (
                    <BulkCompanyEmployeesModal
                        companies={selectedCompanies}
                        employees={users}
                        open={employeesOpen}
                        onOpenChange={setEmployeesOpen}
                        onSaved={exitSelection}
                    />
                )}

                <PaginationControls
                    pagination={paginationMeta}
                    onPageChange={handlePageChange}
                    onPerPageChange={handlePerPageChange}
                    loading={loading}
                    className="mt-6"
                />

                {viewMode === 'grid' && companies.length === 0 && (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <p className="text-muted-foreground">
                                {t('companies.index.empty.no_companies_found', 'No companies found matching your filters.')}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </PageContent>
        </AppLayout>
    );
}
